/**
 * System Termsheet Templates
 *
 * Pre-built loan product templates for common use cases.
 * Users can copy these templates to create their own termsheet configurations.
 *
 * @see Issue #400 - System Templates for First-Time User Experience
 * @see Issue #1309 - Canonical signer policies per template
 */

import type { SignerPolicy } from '../dtos/signer-policy.dto';
import { SignerRole } from '../enums/signer-role';
import type { TermsheetTemplate } from './types';

// =============================================================================
// CANONICAL SIGNER POLICIES (Issue #1309)
// =============================================================================

/**
 * Default loan signer policy: one borrower + optional guarantor +
 * credit-manager counter-sign. Applies to Bridge, DSCR Rental, Construction,
 * and Ground-Up Development templates where a guarantor is common but not
 * strictly required.
 */
const DEFAULT_LOAN_SIGNER_POLICY: SignerPolicy = [
  {
    role: SignerRole.BORROWER,
    cardinality: 'one',
    signingOrder: 1,
    counterSign: false,
    source: 'case.borrowers',
  },
  {
    role: SignerRole.GUARANTOR,
    cardinality: 'optional',
    signingOrder: 2,
    counterSign: false,
    source: 'case.guarantors',
  },
  {
    role: SignerRole.CREDIT_MANAGER,
    cardinality: 'one',
    signingOrder: 3,
    counterSign: true,
    source: 'case.creditManager',
  },
];

/**
 * Fix & Flip signer policy: one borrower + REQUIRED guarantor +
 * credit-manager counter-sign. Fix & Flip loans are asset-based and a
 * personal guarantor is typically mandatory, so the guarantor cardinality
 * is `one` rather than `optional`.
 */
const FIX_AND_FLIP_SIGNER_POLICY: SignerPolicy = [
  {
    role: SignerRole.BORROWER,
    cardinality: 'one',
    signingOrder: 1,
    counterSign: false,
    source: 'case.borrowers',
  },
  {
    role: SignerRole.GUARANTOR,
    cardinality: 'one',
    signingOrder: 2,
    counterSign: false,
    source: 'case.guarantors',
  },
  {
    role: SignerRole.CREDIT_MANAGER,
    cardinality: 'one',
    signingOrder: 3,
    counterSign: true,
    source: 'case.creditManager',
  },
];

/**
 * Borrower-only signer policy (Issue #1531 Phase 10 platform gap #105):
 * one borrower, no guarantor, no lender counter-sign. Used by the
 * "DSCR Rental — Borrower Only" template variant where the loan is fully
 * underwritten on property cash-flow alone and no co-signer is required.
 *
 * This is the only system template that produces a 1-signer DocuSeal
 * envelope; the other 5 default templates all require credit-manager
 * counter-sign and (in some cases) a guarantor.
 */
const BORROWER_ONLY_SIGNER_POLICY: SignerPolicy = [
  {
    role: SignerRole.BORROWER,
    cardinality: 'one',
    signingOrder: 1,
    counterSign: false,
    source: 'case.borrowers',
  },
];

// =============================================================================
// TERMSHEET TEMPLATE IDs
// =============================================================================

export const TERMSHEET_TEMPLATE_IDS = {
  BRIDGE_LOAN: 'tpl-term-bridge-loan',
  FIX_AND_FLIP: 'tpl-term-fix-and-flip',
  DSCR_RENTAL: 'tpl-term-dscr-rental',
  CONSTRUCTION: 'tpl-term-construction',
  GROUND_UP: 'tpl-term-ground-up',
  DSCR_BORROWER_ONLY: 'tpl-term-dscr-borrower-only',
} as const;

// =============================================================================
// TERMSHEET TEMPLATES
// =============================================================================

export const TERMSHEET_TEMPLATES: TermsheetTemplate[] = [
  // ---------------------------------------------------------------------------
  // Bridge Loan
  // ---------------------------------------------------------------------------
  {
    id: TERMSHEET_TEMPLATE_IDS.BRIDGE_LOAN,
    name: 'Bridge Loan',
    description:
      'Short-term financing for acquisition or refinance. Quick close, interest-only payments, flexible exit strategies.',
    category: 'short-term',
    icon: 'swap_horiz',
    color: '#2196F3',
    tags: ['bridge', 'short-term', 'acquisition', 'refinance', 'quick-close'],
    complexity: 'beginner',
    industryFit: ['lending', 'mortgage', 'real_estate'],
    data: {
      loanType: 'bridge',
      loanAmountRange: {
        min: 100000,
        max: 5000000,
      },
      ltvRange: {
        min: 0.5,
        max: 0.8,
      },
      interestRateRange: {
        min: 9.99,
        max: 13.99,
      },
      termMonths: {
        min: 6,
        max: 24,
      },
      originationPoints: {
        min: 1.5,
        max: 3.0,
      },
      fields: [
        {
          name: 'loanAmount',
          label: 'Loan Amount',
          type: 'currency',
          required: true,
          helpText: 'Total loan amount requested',
        },
        {
          name: 'purchasePrice',
          label: 'Purchase Price',
          type: 'currency',
          required: true,
          helpText: 'Property purchase price or current value for refinance',
        },
        {
          name: 'asIsValue',
          label: 'As-Is Value',
          type: 'currency',
          required: true,
          helpText: 'Current appraised value of the property',
        },
        {
          name: 'ltv',
          label: 'Loan-to-Value (LTV)',
          type: 'percent',
          required: true,
          helpText: 'Loan amount divided by property value',
        },
        {
          name: 'interestRate',
          label: 'Interest Rate',
          type: 'percent',
          required: true,
          helpText: 'Annual interest rate',
        },
        {
          name: 'termMonths',
          label: 'Term (Months)',
          type: 'number',
          defaultValue: 12,
          required: true,
          helpText: 'Loan term in months',
        },
        {
          name: 'originationFee',
          label: 'Origination Fee (Points)',
          type: 'percent',
          defaultValue: 2,
          required: true,
          helpText: 'Origination fee as percentage of loan amount',
        },
        {
          name: 'prepaymentPenalty',
          label: 'Prepayment Penalty',
          type: 'select',
          options: ['None', '3 months interest', '6 months interest', 'Step-down'],
          defaultValue: 'None',
          required: true,
          helpText: 'Penalty for early payoff',
        },
        {
          name: 'exitStrategy',
          label: 'Exit Strategy',
          type: 'select',
          options: ['Sale', 'Refinance', 'Cash-out', 'Other'],
          required: true,
          helpText: 'How the borrower plans to repay the loan',
        },
      ],
      keyFeatures: [
        'Close in as fast as 7-10 days',
        'Interest-only payments',
        'No prepayment penalty option available',
        'Asset-based underwriting',
        'Flexible property types accepted',
      ],
      idealBorrower:
        'Experienced investors who need quick capital for acquisition or refinance while arranging permanent financing.',
      signerPolicy: DEFAULT_LOAN_SIGNER_POLICY,
    },
  },

  // ---------------------------------------------------------------------------
  // Fix & Flip
  // ---------------------------------------------------------------------------
  {
    id: TERMSHEET_TEMPLATE_IDS.FIX_AND_FLIP,
    name: 'Fix & Flip',
    description:
      'Short-term financing for property renovation and resale. Includes rehab holdback for construction draws.',
    category: 'short-term',
    icon: 'construction',
    color: '#FF9800',
    tags: ['fix-flip', 'renovation', 'rehab', 'construction', 'arv'],
    complexity: 'intermediate',
    industryFit: ['lending', 'mortgage', 'real_estate'],
    data: {
      loanType: 'fix_and_flip',
      loanAmountRange: {
        min: 75000,
        max: 3000000,
      },
      ltvRange: {
        min: 0.65,
        max: 0.9,
      },
      interestRateRange: {
        min: 10.99,
        max: 14.99,
      },
      termMonths: {
        min: 6,
        max: 18,
      },
      originationPoints: {
        min: 2.0,
        max: 3.5,
      },
      fields: [
        {
          name: 'purchasePrice',
          label: 'Purchase Price',
          type: 'currency',
          required: true,
          helpText: 'Property acquisition price',
        },
        {
          name: 'rehabBudget',
          label: 'Rehab Budget',
          type: 'currency',
          required: true,
          helpText: 'Estimated renovation costs',
        },
        {
          name: 'afterRepairValue',
          label: 'After Repair Value (ARV)',
          type: 'currency',
          required: true,
          helpText: 'Estimated value after renovations complete',
        },
        {
          name: 'acquisitionLoan',
          label: 'Acquisition Loan Amount',
          type: 'currency',
          required: true,
          helpText: 'Amount financed for property purchase',
        },
        {
          name: 'rehabHoldback',
          label: 'Rehab Holdback',
          type: 'currency',
          required: true,
          helpText: 'Amount held for construction draws',
        },
        {
          name: 'totalLoan',
          label: 'Total Loan Amount',
          type: 'currency',
          required: true,
          helpText: 'Acquisition + Rehab holdback',
        },
        {
          name: 'ltc',
          label: 'Loan-to-Cost (LTC)',
          type: 'percent',
          required: true,
          helpText: 'Total loan divided by total project cost',
        },
        {
          name: 'ltvArv',
          label: 'LTV on ARV',
          type: 'percent',
          required: true,
          helpText: 'Total loan divided by after repair value',
        },
        {
          name: 'interestRate',
          label: 'Interest Rate',
          type: 'percent',
          required: true,
        },
        {
          name: 'termMonths',
          label: 'Term (Months)',
          type: 'number',
          defaultValue: 12,
          required: true,
        },
        {
          name: 'drawSchedule',
          label: 'Draw Schedule',
          type: 'select',
          options: ['2 draws', '3 draws', '4 draws', 'Unlimited draws'],
          defaultValue: '3 draws',
          required: true,
          helpText: 'Number of construction draws included',
        },
        {
          name: 'experienceLevel',
          label: 'Borrower Experience',
          type: 'select',
          options: ['First-time', '1-3 flips', '4-10 flips', '10+ flips'],
          required: true,
          helpText: 'Number of completed flips',
        },
      ],
      keyFeatures: [
        'Up to 90% of purchase price financed',
        '100% of rehab costs financed',
        'Fast draw process (48-hour turnaround)',
        'No income verification required',
        'Experience-based pricing tiers',
      ],
      idealBorrower:
        'Real estate investors who buy, renovate, and resell properties. Experience level affects terms and rates.',
      signerPolicy: FIX_AND_FLIP_SIGNER_POLICY,
    },
  },

  // ---------------------------------------------------------------------------
  // DSCR Rental
  // ---------------------------------------------------------------------------
  {
    id: TERMSHEET_TEMPLATE_IDS.DSCR_RENTAL,
    name: 'DSCR Rental',
    description:
      'Long-term financing for investment properties based on property cash flow, not borrower income. Ideal for portfolio builders.',
    category: 'rental',
    icon: 'home_work',
    color: '#4CAF50',
    tags: ['dscr', 'rental', 'long-term', 'cash-flow', 'portfolio'],
    complexity: 'intermediate',
    industryFit: ['lending', 'mortgage', 'real_estate'],
    data: {
      loanType: 'dscr',
      loanAmountRange: {
        min: 100000,
        max: 3000000,
      },
      ltvRange: {
        min: 0.65,
        max: 0.8,
      },
      interestRateRange: {
        min: 7.25,
        max: 9.99,
      },
      termMonths: {
        min: 360,
        max: 360,
      },
      originationPoints: {
        min: 1.0,
        max: 2.5,
      },
      fields: [
        {
          name: 'loanAmount',
          label: 'Loan Amount',
          type: 'currency',
          required: true,
        },
        {
          name: 'propertyValue',
          label: 'Property Value',
          type: 'currency',
          required: true,
          helpText: 'Appraised value or purchase price',
        },
        {
          name: 'ltv',
          label: 'Loan-to-Value (LTV)',
          type: 'percent',
          required: true,
        },
        {
          name: 'monthlyRent',
          label: 'Monthly Rent',
          type: 'currency',
          required: true,
          helpText: 'Actual or market rent',
        },
        {
          name: 'monthlyPitia',
          label: 'Monthly PITIA',
          type: 'currency',
          required: true,
          helpText: 'Principal, Interest, Taxes, Insurance, Association dues',
        },
        {
          name: 'dscr',
          label: 'DSCR Ratio',
          type: 'number',
          required: true,
          helpText: 'Monthly Rent / Monthly PITIA (minimum 1.0)',
        },
        {
          name: 'interestRate',
          label: 'Interest Rate',
          type: 'percent',
          required: true,
        },
        {
          name: 'loanTerm',
          label: 'Loan Term',
          type: 'select',
          options: ['30-year fixed', '30-year 5/1 ARM', '30-year 7/1 ARM', '40-year fixed (interest-only)'],
          defaultValue: '30-year fixed',
          required: true,
        },
        {
          name: 'prepaymentPenalty',
          label: 'Prepayment Penalty',
          type: 'select',
          options: ['None', '3-year step-down', '5-year step-down'],
          defaultValue: '3-year step-down',
          required: true,
        },
        {
          name: 'propertyType',
          label: 'Property Type',
          type: 'select',
          options: ['Single Family', '2-4 Units', 'Condo', 'Townhouse', '5-8 Units'],
          required: true,
        },
      ],
      keyFeatures: [
        'No personal income verification',
        'Based on property cash flow (DSCR)',
        '30-year fixed rate available',
        'No limit on number of properties',
        'Close in LLC or personal name',
      ],
      idealBorrower:
        'Real estate investors building a rental portfolio who want long-term financing based on property performance rather than personal income.',
      signerPolicy: DEFAULT_LOAN_SIGNER_POLICY,
    },
  },

  // ---------------------------------------------------------------------------
  // Construction Loan
  // ---------------------------------------------------------------------------
  {
    id: TERMSHEET_TEMPLATE_IDS.CONSTRUCTION,
    name: 'Construction Loan',
    description:
      'Financing for major renovations or additions to existing structures. Includes inspection-based draw schedule.',
    category: 'development',
    icon: 'engineering',
    color: '#9C27B0',
    tags: ['construction', 'renovation', 'addition', 'development', 'draws'],
    complexity: 'advanced',
    industryFit: ['lending', 'real_estate'],
    data: {
      loanType: 'construction',
      loanAmountRange: {
        min: 250000,
        max: 10000000,
      },
      ltvRange: {
        min: 0.6,
        max: 0.75,
      },
      interestRateRange: {
        min: 11.99,
        max: 15.99,
      },
      termMonths: {
        min: 12,
        max: 24,
      },
      originationPoints: {
        min: 2.0,
        max: 4.0,
      },
      fields: [
        {
          name: 'landValue',
          label: 'Land/Existing Structure Value',
          type: 'currency',
          required: true,
          helpText: 'Current value of land or property before construction',
        },
        {
          name: 'constructionBudget',
          label: 'Construction Budget',
          type: 'currency',
          required: true,
          helpText: 'Total construction/renovation costs',
        },
        {
          name: 'completedValue',
          label: 'Completed Value',
          type: 'currency',
          required: true,
          helpText: 'Projected value upon completion',
        },
        {
          name: 'landLoan',
          label: 'Land/Acquisition Loan',
          type: 'currency',
          required: true,
        },
        {
          name: 'constructionHoldback',
          label: 'Construction Holdback',
          type: 'currency',
          required: true,
          helpText: 'Amount held for construction draws',
        },
        {
          name: 'totalLoan',
          label: 'Total Loan Amount',
          type: 'currency',
          required: true,
        },
        {
          name: 'ltcLand',
          label: 'LTC on Land',
          type: 'percent',
          required: true,
        },
        {
          name: 'ltcConstruction',
          label: 'LTC on Construction',
          type: 'percent',
          required: true,
        },
        {
          name: 'ltv',
          label: 'LTV on Completed Value',
          type: 'percent',
          required: true,
        },
        {
          name: 'interestRate',
          label: 'Interest Rate',
          type: 'percent',
          required: true,
        },
        {
          name: 'termMonths',
          label: 'Term (Months)',
          type: 'number',
          defaultValue: 18,
          required: true,
        },
        {
          name: 'interestReserve',
          label: 'Interest Reserve (Months)',
          type: 'number',
          defaultValue: 6,
          required: false,
          helpText: 'Months of interest included in loan',
        },
        {
          name: 'drawSchedule',
          label: 'Draw Schedule',
          type: 'text',
          required: true,
          helpText: 'Inspection-based, typically 4-6 draws',
        },
        {
          name: 'contingencyReserve',
          label: 'Contingency Reserve',
          type: 'percent',
          defaultValue: 10,
          required: true,
          helpText: 'Percentage of budget held for overruns',
        },
      ],
      keyFeatures: [
        'Up to 75% LTV on completed value',
        '100% of construction costs financed',
        'Inspection-based draw process',
        'Interest reserve option available',
        'Experienced builder required',
      ],
      idealBorrower:
        'Developers and experienced investors doing major renovations or additions. Must have proven construction management experience.',
      signerPolicy: DEFAULT_LOAN_SIGNER_POLICY,
    },
  },

  // ---------------------------------------------------------------------------
  // Ground-Up Development
  // ---------------------------------------------------------------------------
  {
    id: TERMSHEET_TEMPLATE_IDS.GROUND_UP,
    name: 'Ground-Up Development',
    description:
      'New construction financing from land acquisition through vertical construction. Full draw schedule and completion guarantees.',
    category: 'development',
    icon: 'domain_add',
    color: '#F44336',
    tags: ['ground-up', 'new-construction', 'development', 'builder', 'spec'],
    complexity: 'advanced',
    industryFit: ['lending', 'real_estate'],
    data: {
      loanType: 'ground_up',
      loanAmountRange: {
        min: 500000,
        max: 25000000,
      },
      ltvRange: {
        min: 0.55,
        max: 0.7,
      },
      interestRateRange: {
        min: 12.99,
        max: 16.99,
      },
      termMonths: {
        min: 18,
        max: 36,
      },
      originationPoints: {
        min: 2.5,
        max: 4.5,
      },
      fields: [
        {
          name: 'landPurchasePrice',
          label: 'Land Purchase Price',
          type: 'currency',
          required: true,
        },
        {
          name: 'landValue',
          label: 'Land As-Is Value',
          type: 'currency',
          required: true,
          helpText: 'Appraised value of raw land',
        },
        {
          name: 'hardCosts',
          label: 'Hard Costs',
          type: 'currency',
          required: true,
          helpText: 'Direct construction costs',
        },
        {
          name: 'softCosts',
          label: 'Soft Costs',
          type: 'currency',
          required: true,
          helpText: 'Permits, architecture, engineering, etc.',
        },
        {
          name: 'totalProjectCost',
          label: 'Total Project Cost',
          type: 'currency',
          required: true,
          helpText: 'Land + Hard Costs + Soft Costs',
        },
        {
          name: 'completedValue',
          label: 'Completed Value',
          type: 'currency',
          required: true,
          helpText: 'Projected value upon completion',
        },
        {
          name: 'landLoan',
          label: 'Land Loan Amount',
          type: 'currency',
          required: true,
        },
        {
          name: 'constructionLoan',
          label: 'Construction Loan Amount',
          type: 'currency',
          required: true,
        },
        {
          name: 'totalLoan',
          label: 'Total Loan Amount',
          type: 'currency',
          required: true,
        },
        {
          name: 'ltc',
          label: 'Loan-to-Cost',
          type: 'percent',
          required: true,
        },
        {
          name: 'ltv',
          label: 'LTV on Completed Value',
          type: 'percent',
          required: true,
        },
        {
          name: 'interestRate',
          label: 'Interest Rate',
          type: 'percent',
          required: true,
        },
        {
          name: 'termMonths',
          label: 'Term (Months)',
          type: 'number',
          defaultValue: 24,
          required: true,
        },
        {
          name: 'interestReserve',
          label: 'Interest Reserve (Months)',
          type: 'number',
          defaultValue: 12,
          required: false,
        },
        {
          name: 'completionGuarantee',
          label: 'Completion Guarantee',
          type: 'boolean',
          defaultValue: true,
          required: true,
          helpText: 'Personal guarantee for project completion',
        },
        {
          name: 'recourse',
          label: 'Recourse',
          type: 'select',
          options: ['Full Recourse', 'Partial Recourse', 'Non-Recourse (higher rate)'],
          defaultValue: 'Full Recourse',
          required: true,
        },
        {
          name: 'exitStrategy',
          label: 'Exit Strategy',
          type: 'select',
          options: ['Sell', 'Refinance to permanent', 'Hold as rental'],
          required: true,
        },
      ],
      keyFeatures: [
        'Up to 70% LTV on completed value',
        'Up to 85% of total project cost',
        'Interest reserve included',
        'Flexible draw schedule',
        'Pre-sale requirements may apply',
      ],
      idealBorrower:
        'Experienced developers with proven track record of completed ground-up projects. Minimum 3 completed projects typically required.',
      signerPolicy: DEFAULT_LOAN_SIGNER_POLICY,
    },
  },

  // ---------------------------------------------------------------------------
  // DSCR Rental — Borrower Only (Issue #1531 Phase 10, platform gap #105)
  // ---------------------------------------------------------------------------
  // The five system templates above all require a credit-manager counter-sign
  // and (for Fix & Flip) a guarantor. None of them produces a 1-signer
  // DocuSeal envelope, which means the canonical "borrower-only" termsheet
  // workflow had no platform default to reference. This sixth template fills
  // that gap: a DSCR-flavoured termsheet whose `signerPolicy` is a single
  // BORROWER entry, no guarantor and no counter-sign. Backed by
  // `BORROWER_ONLY_SIGNER_POLICY` defined above.
  // ---------------------------------------------------------------------------
  {
    id: TERMSHEET_TEMPLATE_IDS.DSCR_BORROWER_ONLY,
    name: 'DSCR Rental — Borrower Only',
    description:
      'Long-term DSCR financing for an experienced individual borrower with no guarantor or co-signer required. The loan is fully underwritten on property cash-flow.',
    category: 'rental',
    icon: 'home_work',
    color: '#43A047',
    tags: ['dscr', 'rental', 'long-term', 'borrower-only', 'no-guarantor', 'single-signer'],
    complexity: 'beginner',
    industryFit: ['lending', 'mortgage', 'real_estate'],
    data: {
      loanType: 'dscr',
      loanAmountRange: {
        min: 100000,
        max: 1500000,
      },
      ltvRange: {
        min: 0.65,
        max: 0.75,
      },
      interestRateRange: {
        min: 7.5,
        max: 9.99,
      },
      termMonths: {
        min: 360,
        max: 360,
      },
      originationPoints: {
        min: 1.0,
        max: 2.5,
      },
      fields: [
        {
          name: 'loanAmount',
          label: 'Loan Amount',
          type: 'currency',
          required: true,
        },
        {
          name: 'propertyValue',
          label: 'Property Value',
          type: 'currency',
          required: true,
          helpText: 'Appraised value or purchase price',
        },
        {
          name: 'ltv',
          label: 'Loan-to-Value (LTV)',
          type: 'percent',
          required: true,
        },
        {
          name: 'monthlyRent',
          label: 'Monthly Rent',
          type: 'currency',
          required: true,
          helpText: 'Actual or market rent',
        },
        {
          name: 'monthlyPitia',
          label: 'Monthly PITIA',
          type: 'currency',
          required: true,
          helpText: 'Principal, Interest, Taxes, Insurance, Association dues',
        },
        {
          name: 'dscr',
          label: 'DSCR Ratio',
          type: 'number',
          required: true,
          helpText: 'Monthly Rent / Monthly PITIA (minimum 1.1 for borrower-only)',
        },
        {
          name: 'interestRate',
          label: 'Interest Rate',
          type: 'percent',
          required: true,
        },
        {
          name: 'loanTerm',
          label: 'Loan Term',
          type: 'select',
          options: ['30-year fixed', '30-year 5/1 ARM'],
          defaultValue: '30-year fixed',
          required: true,
        },
        {
          name: 'propertyType',
          label: 'Property Type',
          type: 'select',
          options: ['Single Family', '2-4 Units', 'Condo', 'Townhouse'],
          required: true,
        },
      ],
      keyFeatures: [
        'No personal income verification',
        'No guarantor or co-signer required',
        '1-signer envelope — borrower signs alone',
        'Based on property cash flow (DSCR ≥ 1.1)',
        '30-year fixed rate available',
      ],
      idealBorrower:
        'Established real estate investors with strong personal credit and stabilised rental properties — no co-signer needed.',
      signerPolicy: BORROWER_ONLY_SIGNER_POLICY,
    },
  },
];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get all termsheet templates
 */
export function getAllTermsheetTemplates(): TermsheetTemplate[] {
  return TERMSHEET_TEMPLATES;
}

/**
 * Get termsheet template by ID
 */
export function getTermsheetTemplateById(id: string): TermsheetTemplate | undefined {
  return TERMSHEET_TEMPLATES.find((t) => t.id === id);
}

/**
 * Get termsheet templates by category
 */
export function getTermsheetTemplatesByCategory(category: string): TermsheetTemplate[] {
  return TERMSHEET_TEMPLATES.filter((t) => t.category === category);
}

/**
 * Get termsheet templates by loan type
 */
export function getTermsheetTemplatesByLoanType(loanType: string): TermsheetTemplate[] {
  return TERMSHEET_TEMPLATES.filter((t) => t.data.loanType === loanType);
}

/**
 * Search termsheet templates by name, description, or tags
 */
export function searchTermsheetTemplates(query: string): TermsheetTemplate[] {
  const lowerQuery = query.toLowerCase();
  return TERMSHEET_TEMPLATES.filter(
    (t) =>
      t.name.toLowerCase().includes(lowerQuery) ||
      t.description.toLowerCase().includes(lowerQuery) ||
      t.tags.some((tag) => tag.toLowerCase().includes(lowerQuery)),
  );
}
